import { useTranslation } from "react-i18next";
import { OrderInvitationDetailsDTO } from "../../../types";
import { formatBiddingDateTime } from "../../../utils";
import { MaterialType } from "../../../types";

/**
 * Read-only order-invitation summary shown to the anonymous supplier above the offer form:
 * material, quantity, buyer, delivery site, dates, terms, and any attached files. Purely
 * presentational — receives the invitation details DTO and renders them.
 */
const OrderInfoPanel = ({ data }: { data: OrderInvitationDetailsDTO }) => {
  const { t } = useTranslation();

  const row = (labelKey: string, value?: string | null) =>
    value ? (
      <p className="anonymous-offer__row">
        <span className="anonymous-offer__row-label">{t(labelKey)}:</span>{" "}
        {value}
      </p>
    ) : null;

  const deliverySite =
    [data.deliverySiteName, data.deliverySiteAddress, data.deliverySiteCountry]
      .filter(Boolean)
      .join(", ") || data.deliveryAddress;
  // SCRUM-55: a service is performed somewhere, goods are delivered.
  const placeLabel =
    data.materialCategoryType === MaterialType.SERVICES
      ? "tenders.form.servicePlace"
      : "anonymousOffer.deliverySite";

  const dates =
    data.startDate || data.finishDate
      ? [
          data.startDate &&
            `${t("anonymousOffer.startDate")}: ${formatBiddingDateTime(
              data.opensAt,
              data.startDate
            )}`,
          data.finishDate &&
            `${t("anonymousOffer.finishDate")}: ${formatBiddingDateTime(
              data.closesAt,
              data.finishDate
            )}`,
        ]
          .filter(Boolean)
          .join(" — ")
      : "";

  return (
    <section className="anonymous-offer__info">
      <h2 className="anonymous-offer__heading">
        {t("anonymousOffer.orderInfo")}
      </h2>
      {row("anonymousOffer.material", data.materialName)}
      {row("anonymousOffer.category", data.materialCategory)}
      {row("anonymousOffer.description", data.materialDescription)}
      {row(
        "anonymousOffer.quantity",
        `${data.quantity} ${data.measurementUnit ?? ""}`.trim()
      )}
      {row("anonymousOffer.buyer", data.buyerCompanyName)}
      {data.buyerPicksUp
        ? row("anonymousOffer.deliverySite", t("offer.buyerPicksUp"))
        : row(placeLabel, deliverySite || null)}
      {row("anonymousOffer.dates", dates || null)}
      {row("anonymousOffer.incoterm", data.incoterm)}
      {row("anonymousOffer.paymentTerm", data.paymentTerm)}
      {row("anonymousOffer.paymentMethod", data.paymentMethod)}
      {row("anonymousOffer.currency", data.currency)}

      {data.files && data.files.length > 0 && (
        <div className="anonymous-offer__files">
          <p className="anonymous-offer__row-label">
            {t("anonymousOffer.files")}:
          </p>
          <ul>
            {data.files.map((file) => (
              <li key={file.id}>
                <a href={file.url} target="_blank" rel="noopener noreferrer">
                  {file.name}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default OrderInfoPanel;
